import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowLeft, ArrowRight, FileText, Globe, Lock, Mic } from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import { useTherapist } from "../../../../context/useTherapist";
import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import TagPicker from "../../../../shared/ui/form/TagPicker";
import { partitionTags } from "../../../../shared/ui/form/partitionTags";
import {
    CONTENT_LANGUAGES,
    defaultContentLanguage,
    languageLabelKey,
} from "../../../../shared/i18n/contentLanguages";
import { PRESENTING_PROBLEMS } from "../../../../shared/domain/presentingProblems";
import { THERAPY_TYPES } from "../../../../shared/domain/therapyTypes";
import {
    useCreateMeditation,
    usePublishMeditation,
} from "../../application/useMeditations";
import {
    DEFAULT_READING_SPEED_WPM,
    estimateFromScript,
} from "../../domain/readingSpeed";
import {
    BackgroundTrackId,
    MAX_PRESENTING_PROBLEMS,
    MAX_THERAPY_TYPES,
    MEDITATION_LICENSES,
    MeditationLicense,
    PresentingProblem,
    TherapyType,
    isDraftSaveable,
} from "../../domain/types";
import MeditationAudioSection from "../components/MeditationAudioSection";
import MeditationScriptSection from "../components/MeditationScriptSection";
import ReadingSpeedField from "../components/ReadingSpeedField";

/**
 * Which of the two a therapist arrives with — not the stored `kind`, which is
 * derived on the API and may become both once the meditation exists.
 */
type Mode = "text" | "audio";

const STEPS = ["mode", "content", "publish"] as const;

/**
 * Making a meditation, in three steps.
 *
 * The editor shows every field at once, which is right when you come back to
 * work already begun. Starting from nothing it is a wall: a script box, a
 * recorder, a reading pace and a licence, with nothing saying which of them
 * your meditation needs. So the first question is the only one that matters at
 * the start — are you writing it or recording it — and the step after it asks
 * for that alone.
 *
 * Publishing comes last and on its own, because it is a decision about other
 * people seeing your work rather than another field to fill in.
 */
export default function MeditationWizardPage() {
    const { t, i18n } = useTranslation();
    const { therapist } = useTherapist();
    const navigate = useNavigate();
    // `?for=center`: a centre administrator adding to the centre's library.
    // It stays in the centre, so there is nothing to publish.
    const [searchParams] = useSearchParams();
    const forCenter = searchParams.get("for") === "center";
    const createMeditation = useCreateMeditation();
    const publishMeditation = usePublishMeditation();

    const [step, setStep] = useState(0);
    const [mode, setMode] = useState<Mode | null>(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [script, setScript] = useState("");
    const [readingSpeed, setReadingSpeed] = useState(DEFAULT_READING_SPEED_WPM);
    const [backgroundTrack, setBackgroundTrack] = useState<BackgroundTrackId>(
        BackgroundTrackId.NONE
    );
    const [backgroundTrackId, setBackgroundTrackId] = useState<string | null>(null);
    const [mix, setMix] = useState({ voiceVolume: 1, backgroundVolume: 0.25 });
    /** What the author says it runs to, in minutes; null until they say. */
    const [declaredMinutes, setDeclaredMinutes] = useState<number | null>(null);
    /*
     * Defaulted to whatever the app is in. A therapist writing in the Spanish
     * interface is, overwhelmingly, writing in Spanish — and it is a filled-in
     * field they can see and change, not a claim made behind their back.
     */
    const [language, setLanguage] = useState<string>(() =>
        defaultContentLanguage(i18n.language)
    );
    const [audio, setAudio] = useState<{
        path: string;
        durationSeconds: number;
    } | null>(null);
    const [therapyTypes, setTherapyTypes] = useState<TherapyType[]>([]);
    const [customTherapyTypes, setCustomTherapyTypes] = useState<string[]>([]);
    const [problems, setProblems] = useState<PresentingProblem[]>([]);
    const [customProblems, setCustomProblems] = useState<string[]>([]);
    const [publishNow, setPublishNow] = useState(false);
    const [license, setLicense] = useState<MeditationLicense>(
        MeditationLicense.ALL_RIGHTS_RESERVED
    );
    /*
     * Their own name, filled in for them.
     *
     * Publishing requires an author, and the author is nearly always the
     * person filling the form in — asking them to type their own name is a
     * small tax on the one act the library depends on. It is a default in a
     * field they can see and edit, not a claim: a therapist publishing under a
     * practice's name, or not wanting to be named at all, changes or clears it.
     */
    const [authorName, setAuthorName] = useState("");
    /* The therapist arrives after the first render, and once it has been
       filled in it is theirs — a later re-render must not undo an edit, or
       put the name back after they deliberately cleared it. */
    const authorFilled = useRef(false);
    useEffect(() => {
        if (authorFilled.current) return;
        const name = [therapist?.first_name, therapist?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim();
        if (!name) return;
        authorFilled.current = true;
        setAuthorName(name);
    }, [therapist?.first_name, therapist?.last_name]);
    const [error, setError] = useState<string | null>(null);

    /**
     * Choosing again drops what the other route produced. Keeping it would
     * save a recording the therapist last saw two steps ago, under a
     * meditation they came back to write as text.
     */
    const choose = (next: Mode) => {
        setMode(next);
        if (next === "text") setAudio(null);
        else setScript("");
        setStep(1);
    };

    const estimate = useMemo(
        () => estimateFromScript(script, readingSpeed),
        [script, readingSpeed]
    );

    const hasContent = isDraftSaveable({
        title,
        script: mode === "text" ? script : null,
        audioPath: mode === "audio" ? (audio?.path ?? null) : null,
    });

    const create = async () => {
        setError(null);
        try {
            const created = await createMeditation.mutateAsync({
                title: title.trim(),
                description: description.trim() || null,
                script: mode === "text" && script.trim() ? script : null,
                audio_path: mode === "audio" ? (audio?.path ?? null) : null,
                audio_duration_seconds:
                    mode === "audio" ? (audio?.durationSeconds ?? null) : null,
                reading_speed_wpm: readingSpeed,
                background_track: backgroundTrack,
                background_track_id: backgroundTrackId,
                declared_duration_seconds: declaredMinutes
                    ? declaredMinutes * 60
                    : null,
                language: language || null,
                voice_volume: mix.voiceVolume,
                background_volume: mix.backgroundVolume,
                therapy_types: therapyTypes,
                custom_therapy_types: customTherapyTypes,
                presenting_problems: problems,
                custom_presenting_problems: customProblems,
                license,
                author_name: authorName.trim() || null,
                for_center: forCenter || undefined,
            });

            if (publishNow && !forCenter) {
                await publishMeditation.mutateAsync({
                    id: created.id,
                    authorName: authorName.trim() || null,
                    license,
                });
            }

            navigate(`/meditations/${created.id}`, { replace: true });
        } catch {
            setError(
                t(publishNow ? "meditation.publishFailed" : "meditation.saveFailed")
            );
        }
    };

    const working = createMeditation.isPending || publishMeditation.isPending;

    return (
        <>
            <PageMeta
                title={t("meditation.wizard.title")}
                description={t("meditation.toolShort")}
            />

            <div className="mb-6">
                <button
                    type="button"
                    onClick={() =>
                        step === 0 ? navigate("/meditations") : setStep(step - 1)
                    }
                    className="flex min-h-11 items-center gap-2 text-sm font-medium text-content-secondary hover:text-content-primary"
                >
                    <ArrowLeft size={16} aria-hidden="true" />
                    {t(
                        step === 0
                            ? "meditation.backToLibrary"
                            : "meditation.wizard.back"
                    )}
                </button>
            </div>

            <div>
                <h1 className="text-2xl font-semibold text-content-primary">
                    {t(forCenter ? "meditation.wizard.titleCenter" : "meditation.wizard.title")}
                </h1>

                {/*
                  * A list of named steps rather than three coloured bars: the
                  * names are what tell somebody arriving what is still to come,
                  * and `aria-current` says which one this is without asking a
                  * screen reader to infer it from a fill.
                  */}
                <ol
                    aria-label={t("meditation.wizard.steps")}
                    className="mb-8 mt-4 flex flex-wrap gap-2"
                >
                    {STEPS.map((name, index) => (
                        <li
                            key={name}
                            aria-current={index === step ? "step" : undefined}
                            className={`rounded-full px-3 py-1 text-xs font-medium ${
                                index === step
                                    ? "bg-brand-500 text-white"
                                    : index < step
                                      ? "bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400"
                                      : "bg-surface-sunken text-content-tertiary"
                            }`}
                        >
                            {t(`meditation.wizard.stepNames.${name}`)}
                        </li>
                    ))}
                </ol>

                {error && (
                    <p role="alert" className="mb-4 text-sm text-status-critical">
                        {error}
                    </p>
                )}

                {step === 0 && (
                    <section className="space-y-4">
                        <p className="text-base text-content-secondary">
                            {t("meditation.wizard.mode.question")}
                        </p>

                        <div className="grid gap-4 lg:grid-cols-2">
                            {(
                                [
                                    ["text", FileText],
                                    ["audio", Mic],
                                ] as const
                            ).map(([name, Icon]) => (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => choose(name)}
                                    data-testid={`meditation-mode-${name}`}
                                    className="flex flex-col items-start gap-3 rounded-2xl border border-border-subtle bg-surface-raised p-6 text-left transition hover:border-brand-500"
                                >
                                    <span className="flex size-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 dark:bg-brand-500/10">
                                        <Icon size={22} aria-hidden="true" />
                                    </span>
                                    <span className="text-base font-semibold text-content-primary">
                                        {t(`meditation.wizard.mode.${name}.label`)}
                                    </span>
                                    <span className="text-sm text-content-secondary">
                                        {t(`meditation.wizard.mode.${name}.hint`)}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {step === 1 && (
                    <div className="mx-auto max-w-3xl space-y-5">
                        <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                            <Label htmlFor="meditation-title">
                                {t("meditation.fields.title")}
                            </Label>
                            <input
                                id="meditation-title"
                                className="field-base field-default h-11 w-full px-4"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={t("meditation.fields.titlePlaceholder")}
                            />

                            <div className="mt-4">
                                <Label htmlFor="meditation-description">
                                    {t("meditation.fields.description")}
                                </Label>
                                <textarea
                                    id="meditation-description"
                                    rows={2}
                                    className="field-base field-default w-full px-4 py-2.5"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t(
                                        "meditation.fields.descriptionPlaceholder"
                                    )}
                                />
                            </div>

                            <div className="mt-4">
                                <Label htmlFor="meditation-language">
                                    {t("meditation.fields.language")}
                                </Label>
                                <select
                                    id="meditation-language"
                                    className="field-base field-default h-11 w-full appearance-none px-4"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    <option value="">
                                        {t("meditation.fields.languageUnset")}
                                    </option>
                                    {CONTENT_LANGUAGES.map((code) => (
                                        <option key={code} value={code}>
                                            {t(languageLabelKey(code))}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </section>

                        {mode === "text" ? (
                            <>
                                <MeditationScriptSection
                                    script={script}
                                    onChange={setScript}
                                    readingSpeed={readingSpeed}
                                />

                                {/*
                                  * No background track here. A written
                                  * meditation is read aloud by the therapist,
                                  * in a room that already has whatever sound it
                                  * has; the bed is something a recording is
                                  * made over, so it is asked for there.
                                  */}
                                <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                                    <ReadingSpeedField
                                        value={readingSpeed}
                                        onChange={setReadingSpeed}
                                        estimatedSeconds={estimate.seconds}
                                        declaredMinutes={declaredMinutes}
                                        onDeclaredMinutesChange={setDeclaredMinutes}
                                    />
                                    {/*
                                      * Said plainly, because the slider looks
                                      * like a commitment: the pace only sets
                                      * where the prompter starts, and in
                                      * session the therapist taps the paragraph
                                      * they are actually on.
                                      */}
                                    <p className="mt-2 text-xs text-content-tertiary">
                                        {t("meditation.wizard.paceProposal")}
                                    </p>
                                </section>
                            </>
                        ) : (
                            // No bed picker out here: it is chosen in the
                            // studio, against a take that already exists, and
                            // an uploaded file keeps the mix it arrived with.
                            <MeditationAudioSection
                                forCenter={forCenter}
                                backgroundTrack={backgroundTrack}
                                backgroundTrackId={backgroundTrackId}
                                hasStoredAudio={false}
                                onAudioReady={(next) => setAudio(next)}
                                onAudioCleared={() => setAudio(null)}
                                voiceVolume={mix.voiceVolume}
                                backgroundVolume={mix.backgroundVolume}
                                onBackgroundTrackChange={(track, id, next) => {
                                    setBackgroundTrack(track);
                                    setBackgroundTrackId(id);
                                    if (next) setMix(next);
                                }}
                            />
                        )}

                        <div className="flex justify-end">
                            <Button
                                onClick={() => setStep(2)}
                                disabled={!hasContent}
                                endIcon={<ArrowRight size={16} />}
                            >
                                {t("meditation.wizard.next")}
                            </Button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="mx-auto max-w-3xl space-y-5">
                        <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                            <Label>{t("meditation.fields.therapyTypes")}</Label>
                            <TagPicker
                                options={THERAPY_TYPES.map((type) => ({
                                    value: type,
                                    label: t(`session.therapyOptions.${type}`),
                                }))}
                                selected={[...therapyTypes, ...customTherapyTypes]}
                                max={MAX_THERAPY_TYPES}
                                allowCustom
                                browsable={false}
                                onChange={(next) => {
                                    const { known, custom } = partitionTags(
                                        next,
                                        THERAPY_TYPES
                                    );
                                    setTherapyTypes(known as TherapyType[]);
                                    setCustomTherapyTypes(custom);
                                }}
                                searchLabel={t(
                                    "meditation.fields.therapyTypesPlaceholder"
                                )}
                                inputLabel={t(
                                    "meditation.fields.therapyTypesPlaceholder"
                                )}
                                emptyLabel={t("meditation.fields.therapyTypesEmpty")}
                            />
                            <p className="mt-2 text-xs text-content-tertiary">
                                {t("meditation.fields.therapyTypesCustomHint")}
                            </p>

                            <div className="mt-4">
                                <Label>{t("meditation.fields.presentingProblems")}</Label>
                                <TagPicker
                                    options={PRESENTING_PROBLEMS.map((problem) => ({
                                        value: problem,
                                        label: t(`publicDirectory.problems.${problem}`),
                                    }))}
                                    selected={[...problems, ...customProblems]}
                                    max={MAX_PRESENTING_PROBLEMS}
                                    allowCustom
                                    browsable={false}
                                    onChange={(next) => {
                                        const { known, custom } = partitionTags(
                                            next,
                                            PRESENTING_PROBLEMS
                                        );
                                        setProblems(known as PresentingProblem[]);
                                        setCustomProblems(custom);
                                    }}
                                    searchLabel={t(
                                        "meditation.fields.presentingProblemsPlaceholder"
                                    )}
                                    inputLabel={t(
                                        "meditation.fields.presentingProblemsPlaceholder"
                                    )}
                                    emptyLabel={t(
                                        "meditation.fields.presentingProblemsEmpty"
                                    )}
                                />
                            </div>
                        </section>

                        {forCenter ? (
                            <p className="rounded-2xl border border-border-subtle bg-surface-raised p-4 text-sm text-content-secondary">
                                {t("meditation.wizard.centerNotice")}
                            </p>
                        ) : (
                            <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
                                <h2 className="mb-1 text-sm font-semibold text-content-primary">
                                    {t("meditation.publish.title")}
                                </h2>
                                <p className="mb-4 text-xs text-content-tertiary">
                                    {t("meditation.publish.explainer")}
                                </p>

                                {/*
                                  * Two named choices rather than a switch: keeping
                                  * it to yourself is a decision here, not the
                                  * absence of one, and the licence below only means
                                  * anything under the second of them.
                                  */}
                                <div
                                    role="radiogroup"
                                    aria-label={t("meditation.visibilityLabel")}
                                    className="grid gap-3 sm:grid-cols-2"
                                >
                                    {(
                                        [
                                            ["private", Lock, false],
                                            ["public", Globe, true],
                                        ] as const
                                    ).map(([name, Icon, wanted]) => (
                                        <button
                                            key={name}
                                            type="button"
                                            role="radio"
                                            aria-checked={publishNow === wanted}
                                            onClick={() => setPublishNow(wanted)}
                                            data-testid={`meditation-visibility-${name}`}
                                            className={`flex flex-col items-start gap-1.5 rounded-xl border p-4 text-left transition ${
                                                publishNow === wanted
                                                    ? "border-brand-500 bg-brand-50/50 dark:bg-brand-500/10"
                                                    : "border-border-subtle hover:border-brand-500"
                                            }`}
                                        >
                                            <Icon
                                                size={18}
                                                aria-hidden="true"
                                                className="text-brand-500"
                                            />
                                            <span className="text-sm font-medium text-content-primary">
                                                {t(`meditation.visibility.${name}`)}
                                            </span>
                                            <span className="text-xs text-content-tertiary">
                                                {t(`meditation.wizard.visibility.${name}`)}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                <div className="mt-4">
                                    <Label htmlFor="meditation-author">
                                        {t("meditation.fields.authorName")}
                                    </Label>
                                    <input
                                        id="meditation-author"
                                        className="field-base field-default h-11 w-full px-4"
                                        value={authorName}
                                        onChange={(e) => setAuthorName(e.target.value)}
                                        placeholder={t("meditation.fields.authorPlaceholder")}
                                    />
                                </div>

                                <div className="mt-4">
                                    <Label htmlFor="meditation-license">
                                        {t("meditation.fields.license")}
                                    </Label>
                                    <select
                                        id="meditation-license"
                                        className="field-base field-default h-11 w-full appearance-none px-4"
                                        value={license}
                                        onChange={(e) =>
                                            setLicense(e.target.value as MeditationLicense)
                                        }
                                    >
                                        {MEDITATION_LICENSES.map((value) => (
                                            <option key={value} value={value}>
                                                {t(`meditation.licenses.${value}`)}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {publishNow && !authorName.trim() && (
                                    <p className="mt-3 text-xs text-content-tertiary">
                                        {t("meditation.wizard.needsAuthor")}
                                    </p>
                                )}
                            </section>
                        )}

                        <div className="flex justify-end">
                            <Button
                                onClick={() => void create()}
                                loading={working}
                                disabled={publishNow && !forCenter && !authorName.trim()}
                            >
                                {t(
                                    publishNow && !forCenter
                                        ? "meditation.wizard.finishAndPublish"
                                        : "meditation.wizard.finish"
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
